import React, { useEffect, useState } from 'react';

import { ReactComponent as WarningIcon } from 'assets/images/icons/warning.svg';
import { useFormik } from 'formik';
import { t } from 'i18next';
import moment from 'moment';
import PropTypes from 'prop-types';
import { MetaTags } from 'react-meta-tags';
import { connect, useSelector } from 'react-redux';
import { Link, useParams, withRouter } from 'react-router-dom';
import { Button, Container, FormFeedback, FormGroup, Input, Label } from 'reactstrap';

import {
  getNotificationsTypeRequest,
  getOneNotificationRequest,
  validateUserIdsRequest,
} from 'services/notificationsService';
import { addNotification, editNotification } from 'store/actions';

import Breadcrumbs from 'components/Common/Breadcrumb';
import InputWithError from 'components/InputWithError/InputWithError';
import LinksBreadcrumbs from 'components/LinksBreadcrumbs';
import TextEditor from 'components/TextEditor';

import CreateNotificationFormReferralsSelect from './CreateNotificationFormReferralsSelect';
import { formInitialState, handleSubmitNotificationCreateForm } from './utils';
import { createNotificationSchema } from './yupSchema';

const CreateNotification = ({ addNotification, editNotification, history }) => {
  const { notificationId } = useParams();
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [userIdsError, setUserIdsError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validUserIds, setValidUserIds] = useState([]);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [nonExistentIds, setNonExistentIds] = useState([]);
  const language = useSelector((state) => state.Layout.language);
  const BREADCRUMBS_ROUTES = [
    { name: t('notification_title'), path: '/notifications' },
    {
      name: notificationId
        ? `${t('common_edit_notification')}: ${notificationId}`
        : t('common_create_notification'),
    },
  ];

  const redirectHandler = () => {
    return history.push('/notifications');
  };

  const formik = useFormik({
    validationSchema: createNotificationSchema,
    initialValues: formInitialState,
    onSubmit: (values) =>
      handleSubmitNotificationCreateForm(
        values,
        editNotification,
        addNotification,
        redirectHandler,
        validUserIds,
      ),
  });

  useEffect(() => {
    const getNotificationsTypes = async () => {
      const data = await getNotificationsTypeRequest(language);
      setNotificationTypes(data);
    };
    getNotificationsTypes();
  }, [language]);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const validateUserIdsDebounced = (userIdsText) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    setUserIdsError('');
    setValidUserIds([]);
    setNonExistentIds([]);

    if (!userIdsText || !userIdsText.trim()) {
      setIsValidating(false);
      return;
    }

    const timer = setTimeout(() => {
      validateUserIds(userIdsText);
    }, 500);

    setDebounceTimer(timer);
  };

  const validateUserIds = async (userIdsText) => {
    if (!userIdsText || !userIdsText.trim()) {
      setIsValidating(false);
      return;
    }

    const hasWrongSeparators = /[.\s;:|]+/.test(userIdsText) && !userIdsText.includes(',');
    if (hasWrongSeparators) {
      setUserIdsError(t('notification_receiver_type_group_list_wrong_separators_hint'));
      return;
    }

    const hasMixedSeparators =
      userIdsText.includes(',') && /[.\s;:|]+/.test(userIdsText.replace(/,/g, ''));
    if (hasMixedSeparators) {
      setUserIdsError(t('notification_receiver_type_group_list_mixed_separators_hint'));
      return;
    }

    const userIds = userIdsText.includes(',')
      ? userIdsText.split(',').filter((id) => id)
      : [userIdsText].filter((id) => id);

    if (userIds.length === 0) {
      return;
    }

    setIsValidating(true);

    try {
      const response = await validateUserIdsRequest(userIds);

      if (response?.data?.user_ids) {
        setValidUserIds(response.data.user_ids);
      }

      if (
        response?.data?.non_existent_partner_ids &&
        response.data.non_existent_partner_ids.length > 0
      ) {
        setNonExistentIds(response.data.non_existent_partner_ids);
        setUserIdsError(
          t('notification_receiver_type_group_list_wrong_user_hint', {
            users: response.data.non_existent_partner_ids.join(', '),
          }),
        );
      }
    } catch (error) {
      console.error('Ошибка валидации ID пользователей:', error);
      setUserIdsError('Ошибка проверки ID пользователей');
    } finally {
      setIsValidating(false);
    }
  };

  const cleanTextValue = (value) => {
    return value.replace(/\s+/g, '');
  };

  const removeNonExistentIds = () => {
    const currentValue = formik.values.whomNotifyGroupList || '';
    const cleanedValue = cleanTextValue(currentValue);
    const currentIds = cleanedValue.includes(',')
      ? cleanedValue.split(',').filter((id) => id)
      : [cleanedValue].filter((id) => id);

    const validIds = currentIds.filter((id) => !nonExistentIds.includes(id));
    const newValue = validIds.join(',');

    formik.setFieldValue('whomNotifyGroupList', newValue);

    setUserIdsError('');
    setNonExistentIds([]);

    if (newValue) {
      validateUserIdsDebounced(newValue);
    }
  };

  useEffect(() => {
    if (notificationId) {
      localStorage.setItem('editNotificationId', notificationId);

      getOneNotificationRequest(notificationId)?.then((data) => {
        if (data?.id) {
          let recipientCategory = 'all';
          let whomNotifyGroupList = '';

          if (data?.whom_notify.includes('all')) {
            recipientCategory = 'all';
          } else if (data?.whom_notify.length === 1) {
            recipientCategory = 'one';
          } else {
            recipientCategory = 'group';
          }

          formik.setValues({
            recipientCategory,
            id: data.id,
            isEmail: data.isEmail ?? false,
            isSite: data.isSite ?? true,
            notificationDate: moment(new Date(data.notification_date)).format('yyyy-MM-DDThh:mm'),
            notificationTitle: data.notification_title,
            fullMessage: data.notification_text,
            whomNotifyGroup: data?.whom_notify || [],
            whomNotifySolo: recipientCategory === 'one' ? data?.whom_notify[0].toString() : '',
            whomNotifyGroupList: whomNotifyGroupList,
            notificationType: data?.notifications_type?.id,
          });
        }
      });
    }
  }, [notificationId]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>{formik.values.id ? t('common_editing') : t('common_create_notification')}</title>
      </MetaTags>
      <Container fluid>
        <div>
          <Breadcrumbs
            title={t('common_notification')}
            hasBreadcrumbItem={false}
            breadcrumbItem={`${
              formik.values.id ? t('common_edit_notification') : t('common_create_notification')
            }`}
          />
          <LinksBreadcrumbs routes={BREADCRUMBS_ROUTES} className="mb-2" />
        </div>

        <div className="notification__create_body_wrapper">
          <div className="notification__create_body">
            <div className="notification__create_body_upper">
              <div className="notification_create_language_hint_wrapper">
                <div className="notification_create_language_hint_title_wrapper">
                  <div>
                    <WarningIcon color="red" />
                    <h4>{t('notification_create_language_hint_title')}</h4>
                  </div>
                </div>
                <div>{t('notification_create_language_hint_body')}</div>
              </div>
              <FormGroup>
                <div className="notification_create_form_field">
                  <Label>{t('notification_type')}</Label>
                  <Link to="/notifications/types">{t('notification_manage_type')}</Link>
                </div>
                <InputWithError name="notificationType" formik={formik} type="select">
                  <option value="" disabled>
                    {t('notification_choose_type')}
                  </option>
                  {notificationTypes &&
                    notificationTypes.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                </InputWithError>
              </FormGroup>
              <FormGroup>
                <Label>{t('notification_name')}</Label>
                <InputWithError name="notificationTitle" formik={formik} />
              </FormGroup>
              <div className="mb-4">
                <Label>{t('common_message')}</Label>
                <TextEditor
                  value={formik.values.fullMessage}
                  onChange={(value) => formik.setFieldValue('fullMessage', value)}
                  isInvalid={!!formik.errors.fullMessage}
                />
                {formik.errors.fullMessage && (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    {formik.errors.fullMessage}
                  </div>
                )}
              </div>
              <FormGroup>
                <Label>{t('notification_receiver_type')}</Label>
                <div className="notification__create_category_wrapper d-flex justify-content-between align-items-center">
                  <Label>
                    <Input
                      name="recipientCategory"
                      type="radio"
                      checked={formik.values.recipientCategory === 'all'}
                      value="all"
                      onChange={formik.handleChange}
                    />{' '}
                    {t('notification_receiver_type_all')}
                  </Label>
                  <Label>
                    <Input
                      name="recipientCategory"
                      type="radio"
                      value="one"
                      checked={formik.values.recipientCategory === 'one'}
                      onChange={formik.handleChange}
                    />{' '}
                    {t('notification_receiver_type_one')}
                  </Label>
                  <Label>
                    <Input
                      name="recipientCategory"
                      type="radio"
                      value="group"
                      checked={formik.values.recipientCategory === 'group'}
                      onChange={formik.handleChange}
                    />{' '}
                    {t('notification_receiver_type_group')}
                  </Label>
                  <Label>
                    <Input
                      name="recipientCategory"
                      type="radio"
                      value="groupList"
                      checked={formik.values.recipientCategory === 'groupList'}
                      onChange={formik.handleChange}
                    />{' '}
                    {t('notification_receiver_type_group_list')}
                  </Label>
                </div>
              </FormGroup>
              {formik.values?.recipientCategory === 'one' && (
                <div>
                  <Label>{t('notification_receiver_user_id')}</Label>
                  <CreateNotificationFormReferralsSelect
                    editNotificationId={notificationId}
                    formik={formik}
                    name="whomNotifySolo"
                  />
                </div>
              )}
              {formik.values?.recipientCategory === 'group' && (
                <div>
                  <Label>{t('notification_receiver_users_id')}</Label>
                  <CreateNotificationFormReferralsSelect
                    formik={formik}
                    editNotificationId={notificationId}
                    mode="multi"
                    name="whomNotifyGroup"
                  />
                </div>
              )}
              {formik.values?.recipientCategory === 'groupList' && (
                <div style={{ marginBottom: '24px' }}>
                  <Label>{t('notification_receiver_type_group_list_label')}</Label>
                  <div style={{ position: 'relative' }}>
                    <Input
                      type="textarea"
                      name="whomNotifyGroupList"
                      value={formik.values.whomNotifyGroupList || ''}
                      onChange={(e) => {
                        const cleanedValue = cleanTextValue(e.target.value);
                        formik.setFieldValue('whomNotifyGroupList', cleanedValue);
                        validateUserIdsDebounced(cleanedValue);
                      }}
                      placeholder={t('notification_receiver_type_group_list_placeholder')}
                      rows={3}
                      invalid={!!userIdsError}
                      disabled={isValidating}
                      className="whomNotifyGroupList"
                    />
                    {isValidating && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          right: '10px',
                          transform: 'translateY(-50%)',
                          fontSize: '12px',
                          color: '#0e0e0e',
                          fontWeight: 500,
                        }}>
                        {t('common_in_review')}
                      </div>
                    )}
                  </div>
                  {userIdsError && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}>
                        <span>{userIdsError}</span>
                        {nonExistentIds.length > 0 && (
                          <button
                            type="button"
                            onClick={removeNonExistentIds}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginLeft: '10px',
                              padding: '0',
                            }}>
                            {t('common_remove_from_list')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <FormGroup>
                <Label>{t('common_date_and_time')}</Label>
                <Input
                  name="notificationDate"
                  value={formik.values.notificationDate}
                  onChange={formik.handleChange}
                  type="datetime-local"
                  invalid={!!formik.touched.notificationDate && !!formik.errors.notificationDate}
                />
                {formik.touched.notificationDate && formik.errors.notificationDate ? (
                  <FormFeedback type="invalid">{formik.errors.notificationDate}</FormFeedback>
                ) : null}
              </FormGroup>
              <FormGroup>
                <Label>{t('notification_delivery_type')}</Label>
                <div className="d-flex">
                  <Label className="notification__create_checkbox_wrapper d-flex align-items-center">
                    <Input
                      className="notification__create_checkbox"
                      type="checkbox"
                      name="isSite"
                      checked={formik.values.isSite}
                      onChange={formik.handleChange}
                      invalid={!!formik.touched.isSite && !!formik.errors.isSite}
                    />
                    <span>{t('common_site')}</span>
                  </Label>
                  <Label className="notification__create_checkbox_wrapper d-flex align-items-center">
                    <Input
                      className="notification__create_checkbox"
                      type="checkbox"
                      name="isEmail"
                      checked={formik.values.isEmail}
                      onChange={formik.handleChange}
                      invalid={!!formik.touched.isEmail && !!formik.errors.isEmail}
                    />
                    <span>{t('common_mail')}</span>
                  </Label>
                </div>
                {formik.touched.isEmail && formik.errors.isEmail ? (
                  <div className="auth-input-warning-hint">{formik.errors.isEmail}</div>
                ) : null}
              </FormGroup>
            </div>
            <FormGroup>
              <Button
                className="notification__create_btn"
                type="submit"
                color="primary"
                disabled={!!userIdsError || isValidating}
                onClick={formik.handleSubmit}>
                {t('common_save')}
              </Button>
            </FormGroup>
          </div>
        </div>
      </Container>
    </div>
  );
};

CreateNotification.propTypes = {
  addNotification: PropTypes.func,
  editNotification: PropTypes.func,
  getNotification: PropTypes.func,
  history: PropTypes.any,
};

const mapStateToProps = (state) => ({
  getNotification: (notificationId) =>
    state.Notifications.notifications.find((item) => item.id === notificationId),
});

const mapDispatchToProps = (dispatch) => ({
  addNotification: (data) => dispatch(addNotification(data)),
  editNotification: (data) => dispatch(editNotification(data)),
});
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CreateNotification));
